import type { SyncPushResult, SyncPushStoredMediaRef } from '@core/domain/sync/SyncPushResult.ts';
import { unwrapSyncApiEnvelope } from './syncApiEnvelope.ts';

export type SyncPushResponsePayload = {
  acceptedPublicIds?: string[];
  accepted_public_ids?: string[];
  rejectedPublicIds?: Array<{ publicId?: string; public_id?: string; error?: string }>;
  rejected_public_ids?: Array<{ publicId?: string; public_id?: string; error?: string }>;
  storedMedia?: unknown[];
  stored_media?: unknown[];
  totales?: unknown;
};

function mediaRef(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed;
}

function readStringIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0).map((id) => id.trim());
}

function readRejectedRows(value: unknown): Array<{ publicId?: string; public_id?: string; error?: string }> {
  return Array.isArray(value) ? (value as Array<{ publicId?: string; public_id?: string; error?: string }>) : [];
}

function readStoredMediaRows(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function adjuntoAckRef(row: Record<string, unknown>): string {
  if (typeof row.idAdjunto === 'number' && Number.isFinite(row.idAdjunto)) return String(row.idAdjunto);
  return 'stored';
}

/** Contract `{ publicId, firmaRef, fotoRef }` or Inclusion Social `{ origenPublicId, campo, idAdjunto }`. */
export function normalizeStoredMediaRows(rows: unknown[]): SyncPushStoredMediaRef[] {
  const byId = new Map<string, { firmaRef: string | null; fotoRef: string | null }>();

  for (const raw of rows) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const contractPublicId =
      typeof row.publicId === 'string'
        ? row.publicId.trim()
        : typeof row.public_id === 'string'
          ? row.public_id.trim()
          : '';
    if (contractPublicId) {
      const existing = byId.get(contractPublicId) ?? { firmaRef: null, fotoRef: null };
      existing.firmaRef = mediaRef(row.firmaRef) ?? existing.firmaRef;
      existing.fotoRef = mediaRef(row.fotoRef) ?? existing.fotoRef;
      byId.set(contractPublicId, existing);
      continue;
    }

    const origenPublicId = typeof row.origenPublicId === 'string' ? row.origenPublicId.trim() : '';
    if (!origenPublicId) continue;
    const campo = typeof row.campo === 'string' ? row.campo.trim() : '';
    const existing = byId.get(origenPublicId) ?? { firmaRef: null, fotoRef: null };
    const ackRef = adjuntoAckRef(row);
    if (campo === 'firmaBase64' || campo === 'firmaRecibeBase64') {
      existing.firmaRef = existing.firmaRef ?? ackRef;
    } else if (campo === 'fotoBase64' || campo === 'firmaEntregaBase64') {
      existing.fotoRef = existing.fotoRef ?? ackRef;
    }
    byId.set(origenPublicId, existing);
  }

  return [...byId.entries()].map(([publicId, refs]) => ({ publicId, ...refs }));
}

function normalizeSyncPushPayload(payload: SyncPushResponsePayload): SyncPushResponsePayload {
  return {
    acceptedPublicIds: payload.acceptedPublicIds ?? payload.accepted_public_ids,
    rejectedPublicIds: payload.rejectedPublicIds ?? payload.rejected_public_ids,
    storedMedia: payload.storedMedia ?? payload.stored_media,
    totales: payload.totales,
  };
}

export function parseSyncPushResponse(payload: SyncPushResponsePayload): SyncPushResult {
  const normalized = normalizeSyncPushPayload(payload);
  const accepted = readStringIds(normalized.acceptedPublicIds);
  const rejectedPublicIds = readRejectedRows(normalized.rejectedPublicIds)
    .map((row) => ({
      publicId: typeof row.publicId === 'string' ? row.publicId.trim() : typeof row.public_id === 'string' ? row.public_id.trim() : '',
      error: typeof row.error === 'string' && row.error.trim() ? row.error.trim() : 'Rechazado por el servidor.',
    }))
    .filter((row) => row.publicId.length > 0);
  const storedMedia = normalizeStoredMediaRows(readStoredMediaRows(normalized.storedMedia));
  return { acceptedPublicIds: accepted, rejectedPublicIds, storedMedia };
}

/** Inclusion Social envelope: `{ status, code, mensaje, data }`. Errors throw. */
export function parseSyncPushEnvelope(raw: unknown): SyncPushResponsePayload {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('No se pudieron enviar registros: respuesta inválida del servidor.');
  }
  const record = raw as Record<string, unknown>;
  if (record.status === false) {
    const msg =
      typeof record.mensaje === 'string' && record.mensaje.trim()
        ? record.mensaje.trim()
        : 'No se pudieron enviar registros.';
    throw new Error(msg);
  }
  const code = record.code;
  if (typeof code === 'number' && code >= 400) {
    const msg =
      typeof record.mensaje === 'string' && record.mensaje.trim()
        ? record.mensaje.trim()
        : `No se pudieron enviar registros (código ${code}).`;
    throw new Error(msg);
  }
  const inner = unwrapSyncApiEnvelope(raw) as SyncPushResponsePayload;
  const totales = record.totales ?? inner.totales;
  return { ...inner, totales };
}

function publicIdsFromPushSection(body: Record<string, unknown>, section: 'entregas' | 'reposiciones'): string[] {
  const rows = body[section];
  if (!Array.isArray(rows)) return [];
  const ids: string[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const publicId = (row as { publicId?: unknown }).publicId;
    if (typeof publicId === 'string' && publicId.trim()) ids.push(publicId.trim());
  }
  return ids;
}

function readTotalesCount(totales: unknown, keys: readonly string[]): number {
  if (typeof totales === 'number' && Number.isFinite(totales)) return Math.max(0, Math.trunc(totales));
  if (!totales || typeof totales !== 'object' || Array.isArray(totales)) return 0;
  const record = totales as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.trunc(value));
  }
  return 0;
}

/** Inclusion Social may answer with `totales` instead of explicit `acceptedPublicIds`. */
export function inferAcceptedPublicIdsFromTotales(
  payload: SyncPushResponsePayload,
  requestBody: Record<string, unknown>,
): string[] {
  const totales = payload.totales;
  if (totales == null) return [];

  const sentEntregas = publicIdsFromPushSection(requestBody, 'entregas');
  const sentReposiciones = publicIdsFromPushSection(requestBody, 'reposiciones');
  const sentAll = [...sentEntregas, ...sentReposiciones];
  if (sentAll.length === 0) return [];

  const rejected = new Set(parseSyncPushResponse(payload).rejectedPublicIds.map((row) => row.publicId));
  const acceptSent = (ids: readonly string[]) => ids.filter((id) => !rejected.has(id));

  const entregaCount = readTotalesCount(totales, ['entregas', 'entregasAceptadas', 'entregas_aceptadas', 'totalEntregas']);
  const reposicionCount = readTotalesCount(totales, [
    'reposiciones',
    'reposicionesAceptadas',
    'reposiciones_aceptadas',
    'totalReposiciones',
  ]);
  const totalCount = readTotalesCount(totales, ['total', 'aceptados', 'totalAceptados', 'total_aceptados']);

  const accepted: string[] = [];
  if (entregaCount > 0 && sentEntregas.length > 0) {
    accepted.push(...acceptSent(sentEntregas.slice(0, Math.min(entregaCount, sentEntregas.length))));
  }
  if (reposicionCount > 0 && sentReposiciones.length > 0) {
    accepted.push(...acceptSent(sentReposiciones.slice(0, Math.min(reposicionCount, sentReposiciones.length))));
  }
  if (accepted.length > 0) return accepted;

  if (totalCount > 0) {
    return acceptSent(sentAll.slice(0, Math.min(totalCount, sentAll.length)));
  }

  // Full batch accepted when totales counts cover all sent rows.
  if (entregaCount >= sentEntregas.length && reposicionCount >= sentReposiciones.length && entregaCount + reposicionCount > 0) {
    return acceptSent(sentAll);
  }

  return [];
}

export function resolveSyncPushResult(
  payload: SyncPushResponsePayload,
  requestBody?: Record<string, unknown>,
): SyncPushResult {
  const parsed = parseSyncPushResponse(payload);
  if (parsed.acceptedPublicIds.length > 0 || !requestBody) return parsed;
  const inferred = inferAcceptedPublicIdsFromTotales(payload, requestBody);
  if (inferred.length === 0) return parsed;
  return { ...parsed, acceptedPublicIds: inferred, acceptedInferredFromTotales: true };
}
